/**
 * Scroll reveal for .rf-reveal elements.
 *
 * Ported from the design prototype (1.homepage.html), with two fixes the
 * original needs before it can live in a Shopify theme:
 *
 *  1. The prototype binds once at page load. The theme editor injects and
 *     re-renders sections without a reload, so anything added afterwards would
 *     keep its initial opacity:0 forever and the section would look empty.
 *     We re-scan on the editor's own lifecycle events.
 *
 *  2. In design mode, reveal-on-scroll actively fights editing: a merchant adds
 *     a section below the fold and sees nothing. There, newly loaded content is
 *     revealed immediately rather than waiting for an intersection.
 *
 * Whether an element reveals at all is decided in Liquid — a section only emits
 * the .rf-reveal class when the theme's motion setting is on. This file never
 * reads theme settings.
 */
(function () {
  'use strict';

  var SELECTOR = '.rf-reveal:not(.in)';
  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  var prefersReduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var supported = 'IntersectionObserver' in window;

  function reveal(el) {
    el.classList.add('in');
  }

  var observer = null;

  if (supported && !prefersReduced) {
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            reveal(entries[i].target);
            observer.unobserve(entries[i].target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
  }

  /**
   * @param {ParentNode} root    Subtree to scan. Defaults to the document.
   * @param {boolean} immediate  Skip the animation and reveal straight away.
   */
  function scan(root, immediate) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll(SELECTOR);

    // A section/block root can itself carry .rf-reveal, and querySelectorAll
    // on that element would not include it.
    var list = Array.prototype.slice.call(nodes);
    if (scope !== document && scope.classList && scope.classList.contains('rf-reveal')) {
      list.push(scope);
    }

    for (var i = 0; i < list.length; i++) {
      if (immediate || !observer) {
        reveal(list[i]);
      } else {
        observer.observe(list[i]);
      }
    }
  }

  function init() {
    scan(document, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor lifecycle. In design mode we reveal immediately so the merchant
  // never sees an empty section they just added.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target, DESIGN_MODE);
  });

  document.addEventListener('shopify:block:added', function (event) {
    scan(event.target, DESIGN_MODE);
  });

  // Selecting a section must always make it visible, per Shopify's guidance.
  document.addEventListener('shopify:section:select', function (event) {
    scan(event.target, true);
  });

  document.addEventListener('shopify:block:select', function (event) {
    scan(event.target, true);
  });

  // Stop observing a removed subtree. Without this, an editor session that adds
  // and removes sections accumulates observed nodes that are no longer in the
  // document -- the observer holds them alive.
  document.addEventListener('shopify:section:unload', function (event) {
    if (!observer || !event.target || !event.target.querySelectorAll) return;
    var nodes = event.target.querySelectorAll('.rf-reveal');
    for (var i = 0; i < nodes.length; i++) observer.unobserve(nodes[i]);
    if (event.target.classList && event.target.classList.contains('rf-reveal')) {
      observer.unobserve(event.target);
    }
  });
})();
